// v294b-image-components
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TicketHeroStrip } from '../raffle/TicketBanner';
import { RaffleHeroStrip } from '../raffle/RaffleBanner';
import OptImg from './OptImg';

const API = process.env.REACT_APP_BACKEND_URL;

const fullImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API}${url}`;
};

/** Aktions-Banner-Slider: rotiert hochgeladene Aktionsbanner gemeinsam mit
 *  dem Lose- (TicketHeroStrip) und Gewinnspiel-Banner (RaffleHeroStrip). */
export default function PromoBannerSlider() {
  const [promos, setPromos] = useState([]);
  const [ticketActive, setTicketActive] = useState(false);
  const [raffleActive, setRaffleActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/shop/promo-banners`)
      .then(r => r.ok ? r.json() : { banners: [] })
      .then(d => { if (!cancelled) setPromos(d.banners || []); })
      .catch(() => {});
    fetch(`${API}/api/tickets/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => { if (!cancelled) setTicketActive(!!(d && d.active)); })
      .catch(() => {});
    fetch(`${API}/api/raffle/active`)
      .then(r => r.ok ? r.json() : { active: false })
      .then(d => { if (!cancelled) setRaffleActive(!!(d && d.active)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const slides = useMemo(() => {
    const list = promos.map(p => ({ type: 'promo', data: p, key: `promo-${p.id}` }));
    if (ticketActive) list.push({ type: 'ticket', key: 'ticket' });
    if (raffleActive) list.push({ type: 'raffle', key: 'raffle' });
    return list;
  }, [promos, ticketActive, raffleActive]);

  const count = slides.length;

  useEffect(() => {
    if (idx >= count && count > 0) setIdx(0);
  }, [count, idx]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const t = setInterval(() => setIdx(i => (i + 1) % count), 6500);
    return () => clearInterval(t);
  }, [count, paused]);

  const go = useCallback((delta) => setIdx(i => (i + delta + count) % count), [count]);

  if (count === 0) return null;
  const active = slides[Math.min(idx, count - 1)];

  return (
    <div
      className="promo-slider"
      data-testid="promo-banner-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{ position: 'relative', margin: '8px 0 16px' }}
    >
      <div data-testid={`promo-slide-${active.key}`}>
        {active.type === 'ticket' && <TicketHeroStrip />}
        {active.type === 'raffle' && <RaffleHeroStrip />}
        {active.type === 'promo' && <PromoSlide banner={active.data} />}
      </div>

      {count > 1 && (
        <>
          <button
            className="promo-slider-arrow promo-slider-prev"
            onClick={() => go(-1)}
            aria-label="Vorheriger Banner"
            data-testid="promo-slider-prev"
            style={arrowStyle('left')}
          ><ChevronLeft size={20} /></button>
          <button
            className="promo-slider-arrow promo-slider-next"
            onClick={() => go(1)}
            aria-label="Naechster Banner"
            data-testid="promo-slider-next"
            style={arrowStyle('right')}
          ><ChevronRight size={20} /></button>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10 }}>
            {slides.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setIdx(i)}
                aria-label={`Banner ${i + 1}`}
                data-testid={`promo-slider-dot-${i}`}
                style={{
                  width: i === idx ? 22 : 8, height: 8, borderRadius: 4, border: 'none',
                  background: i === idx ? '#EC2229' : '#444', cursor: 'pointer',
                  transition: 'width 0.3s, background-color 0.3s', padding: 0,
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function arrowStyle(side) {
  return {
    position: 'absolute', top: '38%', [side]: 6, transform: 'translateY(-50%)',
    width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(0,0,0,0.55)', color: '#fff', display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', zIndex: 3, backdropFilter: 'blur(4px)',
  };
}

function PromoSlide({ banner }) {
  const hasText = banner.title || banner.subtitle || (banner.cta_text && banner.link);
  const inner = (
    <div
      style={{
        position: 'relative', borderRadius: 14, overflow: 'hidden',
        border: '1px solid #222',
        background: banner.image_url ? '#0e0e0e' : (banner.bg_color || '#0e0e0e'),
        minHeight: banner.image_url ? undefined : 180,
      }}
    >
      {banner.image_url && (
        <OptImg
          src={fullImg(banner.image_url)}
          alt={banner.title || 'Aktionsbanner'}
          width={1600}
          sizes="(max-width: 900px) 100vw, 1600px"
          priority
          style={{ width: '100%', height: 'auto', display: 'block' }}
          data-testid="promo-slide-image"
        />
      )}
      {hasText && (
        <div
          style={{
            position: banner.image_url ? 'absolute' : 'static',
            left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'flex-end',
            background: banner.image_url
              ? 'linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 100%)'
              : 'transparent',
          }}
        >
          <div style={{ padding: '24px 28px', maxWidth: 640 }}>
            {banner.title && (
              <h2 style={{ margin: 0, fontSize: 'clamp(20px, 3vw, 30px)', fontWeight: 800, color: '#fff', lineHeight: 1.15 }}>
                {banner.title}
              </h2>
            )}
            {banner.subtitle && (
              <p style={{ margin: '8px 0 0', color: '#d6d6d6', fontSize: 15, maxWidth: 520 }}>
                {banner.subtitle}
              </p>
            )}
            {banner.cta_text && banner.link && (
              <span
                style={{
                  display: 'inline-block', marginTop: 16, padding: '10px 22px',
                  background: '#EC2229', color: '#fff', borderRadius: 999, fontWeight: 700,
                  fontSize: 14, textDecoration: 'none',
                }}
              >{banner.cta_text} →</span>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (!banner.link) return inner;
  const isExternal = banner.link.startsWith('http');
  if (isExternal) {
    return (
      <a href={banner.link} target="_blank" rel="noopener noreferrer"
         style={{ textDecoration: 'none' }} data-testid="promo-slide-link">{inner}</a>
    );
  }
  return <Link to={banner.link} style={{ textDecoration: 'none' }} data-testid="promo-slide-link">{inner}</Link>;
}
